import React, { useState, useEffect } from "react";
import "../../styles/SearchResultsRHS.css";
import TableComponent from "../../components/UI/TableComponent.tsx";
import {
  CLAIMS_AMOUNT_DISBURSED_HEADERS,
  CLAIMS_CLAIM_COUNT_HEADERS,
  CLAIMS_CLAIM_PROCESSED_HEADERS,
  CLAIMS_CLAIM_RADIO_HEADERS,
  CLAIMS_CUSTOMER_COUNT_HEADERS,
  CUSTOMER_COUNT_HEADERS,
  getCountryStateTableHeader,
  INSURED_AREA_HEADER,
  POLICIES_HEADERS,
  POSP_ARABLE_HEADERS,
  POSP_HEADERS,
  POSP_LOCATION,
  POSP_TARGET_HEADERS,
  PREMIUM_HEADERS,
  SUM_INSURED_HEADERS,
} from "../../utils/TableHeaders.tsx";
import Map from "../../components/UI/Map.tsx";
import { useDispatch, useSelector } from "react-redux";
import {
  getGridLatLng,
  getSelectedGeometry,
  getStateData,
} from "../../services/MapApi.tsx";
import { ColumnHeaderType, OptionType } from "../../utils/Types.tsx";
import {
  ACTIVE_VIEW_STATE,
  COLOR,
  COMBINED_LEVELS_FILTER,
  AGGREGATED_TITLE,
  TABLE_SORT_ASC,
  COUNTRY,
  ACTIVE_VIEW_COUNTRY,
  MAP_VIEW,
  TABLE_SORT_DESC,
  ALL_PRODUCT,
  STATE_ARRAY,
  REAL_TIME_LOCATION,
  MARKER_TYPE,
  DATE_FORMAT,
} from "../../utils/Constants.tsx";
import {
  formatNumber,
  getDataWithKeyValue,
  numberFormatWithoutCurrency,
  numberFormatWithCurrency,
  sortListItems,
  parseStateData,
  getRequestParams,
  getValueKey,
  sumAll,
} from "../../utils/Util.tsx";
import {
  handleStateGridLatLng,
  handleStateSelectedGeometry,
  handleStoreMarkers,
  updateLegendValue,
} from "../../redux/slices/mapApiSlice.ts";
import { handleCountryGridLatLng } from "../../redux/slices/countryApiSlice.ts";
import moment, { Moment } from "moment";
import { DateHourMinutePlayer } from "../../components/UI/DateHourMinutePlayer.tsx";
import {
  WeatherLayerDropdown,
  WeatherLayerItem,
} from "../../components/UI/WeatherLayerDropdown.tsx";

import { ReactComponent as IconRain } from "../../assets/images/precipitation.svg";
import { ReactComponent as IconWind } from "../../assets/images/wind.svg";
import { ReactComponent as IconCyclone } from "../../assets/images/cyclone.svg";
import { ReactComponent as IconTemp } from "../../assets/images/temperature.svg";
import { ReactComponent as IconHumidity } from "../../assets/images/humidity.svg";

const getGridIdWiseData = (list, levelOne, levelTwo) => {
  const result = list?.reduce((acc, item) => {
    acc[item.gridid] = { ...item, levelOne, levelTwo };
    return acc;
  }, {});
  return result;
};

const getStateIdWiseDataWithSum = (
  list,
  keyName,
  valueName,
  levelOne,
  levelTwo
) => {
  const result = list?.reduce((acc, item) => {
    const key = item[keyName];
    if (!acc[key]) {
      acc[key] = {
        [keyName]: key,
        total: 0,
        levelOne: levelOne,
        levelTwo: levelTwo,
      };
    }
    if (valueName === "payout_amounts") {
      acc[key].total += Number(sumAll(item[valueName]) || 0);
    } else {
      acc[key].total += Number(item[valueName] || 0);
    }

    return acc;
  }, {});

  if (result) {
    const formattedResultList = Object.entries(result).map(([, data]: any) => {
      return {
        [keyName]: data?.sid,
        total: data.total,
      };
    });
    const sortedArray = sortListItems(
      formattedResultList,
      "total",
      TABLE_SORT_DESC
    );
    return {
      data: result,
      array: sortedArray,
      max: sortedArray && sortedArray[0] ? sortedArray[0].total : 0,
      min:
        sortedArray && sortedArray[sortedArray.length - 1]
          ? sortedArray[sortedArray.length - 1].total
          : 0,
    };
  }
};

interface SearchResultsRHSProps {
  selectedFilters: any;
  setGridId: (data: string) => void;
  gridId: string;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  distanceRange: Number;
  stateFilter: string;
}

const StateMap = ({
  selectedFilters,
  setGridId,
  gridId,
  isLoading,
  setIsLoading,
  distanceRange,
  stateFilter,
}: SearchResultsRHSProps) => {
  const [columnHeaders, setColumnHeaders] = useState<ColumnHeaderType[]>([]);
  const [tableTitle, setTableTitle] = useState("");
  const [mapClickedInfo, setMapClickedInfo] = useState(null);
  const markersStore = useSelector((state: any) => state.map.markersStore);
  const [countryTableData, setCountryTableData] = useState([]);
  const [canShowToolTip, setCanShowToolTip] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [idWiseData, setIdWiseData] = useState(null);
  const [stateTableData, setStateTableData] = useState(null);
  const [mapToolTip, setMapToolTip] = useState(null);
  const [markerType, setMarkersType] = useState<string[]>([]);
  const [legendStyle, setLegendStyle] = useState(null);
  const [aggregatedValue, setAggregatedValue] = useState(null);
  const [aggregatedTitle, setAggregatedTitle] = useState(null);
  const [averageCatchmentRatio, setAverageCatchmentRatio] = useState(null);
  const [sortBy, setSortBy] = useState<string | null>();
  const [isGridViewSelected, setGridViewSelected] = useState(false);
  const [sortOrder, setSortOrder] = useState<string>(TABLE_SORT_ASC);
  //const [forecastDateList, setForecastDateList] = useState<any[]>([]);
  //const [dt, setDt] = useState(new Date());
  const [forecastDate, setForecastDate] = useState<Moment>(moment());
  const activeView = useSelector((state: any) => state?.filter?.activeView);
  const computedDate = useSelector((state: any) => state?.map?.computedDate);
  const dispatch: any = useDispatch();
  const statesWithId = useSelector(
    (state: any) => state?.filter?.statesDataWithId
  );
  const districtsWithId = useSelector(
    (state: any) => state?.filter?.districtsDataWithId
  );
  const { stateViewData, selectedGeometry, legendValues } = useSelector(
    (state: any) => state.map
  );
  const productListWithId = useSelector(
    (state: any) => state?.filter?.products
  );
  const seasonListWithId = useSelector((state: any) => state?.filter?.seasons);
  const gridLatLng = useSelector((state: any) => state?.map?.gridLatLng);

  const items: WeatherLayerItem[] = [
    {
      id: "precipitation",
      label: "Precipitation",
      icon: <IconRain />,
    },
    { id: "wind", label: "Wind", icon: <IconWind /> },
    { id: "cyclone", label: "Cyclone", icon: <IconCyclone /> },
    { id: "temperature", label: "Temperature", icon: <IconTemp /> },
    { id: "humidity", label: "Humidity", icon: <IconHumidity /> },
  ];

  const [selected, setSelected] = useState<string>("precipitation");

  useEffect(() => {
    dispatch(handleStoreMarkers({ marker: "state", res: STATE_ARRAY }));
    if (!markerType.includes("state")) {
      setMarkersType("state");
    }
  }, []);

  useEffect(() => {
    if (mapToolTip?.gridid) {
      setGridId(mapToolTip.gridid);
    }
  }, [mapToolTip]);

  useEffect(() => {
    if (mapClickedInfo?.gridid && canShowToolTip) {
      setMapToolTip({
        ...idWiseData[mapClickedInfo?.gridid],
        gridid: mapClickedInfo?.gridid,
      });
      dispatch(
        getSelectedGeometry(
          { lat: mapClickedInfo.lat, lng: mapClickedInfo.lng },
          activeView
        )
      );
    }
    if (stateViewData) {
      //dispatch(handleStateGridLatLng(null));
      updateTableData(mapClickedInfo?.gridid, "gridid");
    }
  }, [mapClickedInfo]);

  useEffect(() => {
    if (!gridId && stateViewData?.data) {
      updateTableData();
      dispatch(handleStateGridLatLng(null));
    }
  }, [gridId]);

  const getStateRangeValue = (minValue, maxValue) => {
    return {
      minValue: formatNumber(minValue, 2),
      maxValue: formatNumber(maxValue, 2),
      minColor:
        activeView === ACTIVE_VIEW_COUNTRY
          ? COLOR.country.min
          : COLOR.state.min,
      maxColor:
        activeView === ACTIVE_VIEW_COUNTRY
          ? COLOR.country.max
          : COLOR.state.max,
    };
  };
  const isCountryStateView = () => {
    return !isGridViewSelected && stateFilter === COUNTRY;
  };

  const updateHeader = (
    levelOne: string,
    levelTwo: string,
    aggregated: any
  ) => {
    switch (levelOne + "-" + levelTwo) {
      case COMBINED_LEVELS_FILTER.POSPm_POSP:
        setAggregatedTitle(AGGREGATED_TITLE.total_posps);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_posps)
        );
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("POSP Count"));
        } else {
          setColumnHeaders(POSP_HEADERS);
        }
        setTableTitle("POSP'S DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_posp_count,
            legendValues?.max_posp_count
          )
        );
        break;
      case COMBINED_LEVELS_FILTER.POSPm_TARGET:
        setAggregatedTitle(AGGREGATED_TITLE.total_target_value);
        setAggregatedValue(
          numberFormatWithCurrency(aggregated?.total_target_value)
        );
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("Target Value"));
        } else {
          setColumnHeaders(POSP_TARGET_HEADERS);
        }
        setTableTitle("TARGET DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_target_value,
            legendValues?.max_target_value
          )
        );
        break;
      case COMBINED_LEVELS_FILTER.POSPm_ARABLE:
        setAggregatedTitle(AGGREGATED_TITLE.total_arable_area + "(acres)");
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_arable_area)
        );
        setAverageCatchmentRatio(aggregated?.average_catchment_ratio);
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("Arable Area"));
        } else {
          setColumnHeaders(POSP_ARABLE_HEADERS);
        }
        setTableTitle("ARABLE DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_catchment_ratio,
            legendValues?.max_catchment_ratio
          )
        );
        break;
      case COMBINED_LEVELS_FILTER.POSPm_LOCATION:
        setAggregatedTitle("");
        setAggregatedValue("");
        setAverageCatchmentRatio("");
        setColumnHeaders(POSP_LOCATION);
        setTableTitle("POSP DETAILS");
        setLegendStyle(null);
        break;
      case COMBINED_LEVELS_FILTER.Policy_Customer_Count:
        setAggregatedTitle(AGGREGATED_TITLE.total_customers);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_customers)
        );
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("Customer Count"));
        } else {
          setColumnHeaders(CUSTOMER_COUNT_HEADERS);
        }
        setTableTitle("CUSTOMER COUNT DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_customers,
            legendValues?.max_customers
          )
        );
        break;
      case COMBINED_LEVELS_FILTER.Policy_Policy_Count:
        setAggregatedTitle(AGGREGATED_TITLE.total_policies);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_policies)
        );
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("Policy Count"));
        } else {
          setColumnHeaders(POLICIES_HEADERS);
        }

        setTableTitle("POLICIES DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_policies,
            legendValues?.max_policies
          )
        );
        break;
      case COMBINED_LEVELS_FILTER.Policy_Premium:
        setAggregatedTitle(AGGREGATED_TITLE.total_premium);
        setAggregatedValue(numberFormatWithCurrency(aggregated?.total_premium));
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("Premium"));
        } else {
          setColumnHeaders(PREMIUM_HEADERS);
        }
        setTableTitle("PREMIUM DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_premium,
            legendValues?.max_premium
          )
        );
        break;
      case COMBINED_LEVELS_FILTER.Policy_Sum_Insured:
        setAggregatedTitle(AGGREGATED_TITLE.total_suminsured);
        setAggregatedValue(
          numberFormatWithCurrency(aggregated?.total_suminsured)
        );
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("Sum Insured"));
        } else {
          setColumnHeaders(SUM_INSURED_HEADERS);
        }
        setTableTitle("SUM INSURED DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_suminsured,
            legendValues?.max_suminsured
          )
        );
        break;
      case COMBINED_LEVELS_FILTER.Policy_Insured_Area:
        setAggregatedTitle(AGGREGATED_TITLE.total_insured_area + "(acres)");
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_insured_area)
        );
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("Insured Area"));
        } else {
          setColumnHeaders(INSURED_AREA_HEADER);
        }
        setTableTitle("INSURED AREA DETAILS");
        setLegendStyle(
          getStateRangeValue(legendValues?.min_area, legendValues?.max_area)
        );
        break;
      case COMBINED_LEVELS_FILTER.Claims_Claim_Processed:
        setAggregatedTitle(AGGREGATED_TITLE.total_claims);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_claims)
        );
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("Claim Processed"));
        } else {
          setColumnHeaders(CLAIMS_CLAIM_PROCESSED_HEADERS);
        }
        setTableTitle("CLAIM PROCESSED DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_claim_count,
            legendValues?.max_claim_count
          )
        );
        break;
      case COMBINED_LEVELS_FILTER.Claims_Claim_Pending:
        setAggregatedTitle(AGGREGATED_TITLE.total_claims);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_claims)
        );
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("Claim Pending"));
        } else {
          setColumnHeaders(CLAIMS_CLAIM_PROCESSED_HEADERS);
        }
        setTableTitle("CLAIM PENDING DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_claim_count,
            legendValues?.max_claim_count
          )
        );
        break;
      case COMBINED_LEVELS_FILTER.Claims_Customer_Count:
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("Customer Count"));
        } else {
          setColumnHeaders(CLAIMS_CUSTOMER_COUNT_HEADERS);
        }
        setTableTitle("CUSTOMER COUNT DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_customer_count,
            legendValues?.max_customer_count
          )
        );
        break;
      case COMBINED_LEVELS_FILTER.Claims_Amount_Disbursed:
        setAggregatedTitle(AGGREGATED_TITLE.total_payout);
        setAggregatedValue(numberFormatWithCurrency(aggregated?.total_payout));
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("Amount Disbursed"));
        } else {
          setColumnHeaders(CLAIMS_AMOUNT_DISBURSED_HEADERS);
        }
        setTableTitle("AMOUNT DISBURSED DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_payout_amount,
            legendValues?.max_payout_amount
          )
        );
        break;
      case COMBINED_LEVELS_FILTER.Claims_Amount_Can_Be_Disbursed:
        setAggregatedTitle(AGGREGATED_TITLE.total_payout);
        setAggregatedValue(numberFormatWithCurrency(aggregated?.total_payout));
        if (isCountryStateView()) {
          setColumnHeaders(
            getCountryStateTableHeader("Amount Can Be Disbursed")
          );
        } else {
          setColumnHeaders(CLAIMS_AMOUNT_DISBURSED_HEADERS);
        }
        setTableTitle("AMOUNT CAN BE DISBURSED DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_payout_amount,
            legendValues?.max_payout_amount
          )
        );
        break;
      case COMBINED_LEVELS_FILTER.Claims_Loss_Ratio:
        setAggregatedTitle(AGGREGATED_TITLE.average_loss_ratio);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.average_loss_ratio)
        );
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("Loss Ratio"));
        } else {
          setColumnHeaders(CLAIMS_CLAIM_RADIO_HEADERS);
        }
        setTableTitle("LOSS RATIO(CLAIM/PREMIUM) DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_loss_ratio,
            legendValues?.max_loss_ratio
          )
        );
        break;
      case COMBINED_LEVELS_FILTER.Claims_Total_Claim_Count:
        setAggregatedTitle(AGGREGATED_TITLE.total_claims);
        setAggregatedValue(
          numberFormatWithoutCurrency(aggregated?.total_claims)
        );
        if (isCountryStateView()) {
          setColumnHeaders(getCountryStateTableHeader("Claim_Count"));
        } else {
          setColumnHeaders(CLAIMS_CLAIM_COUNT_HEADERS);
        }
        setTableTitle("TOTAL CLAIM COUNT DETAILS");
        setLegendStyle(
          getStateRangeValue(
            legendValues?.min_claim_count,
            legendValues?.max_claim_count
          )
        );
        break;
      default:
        setColumnHeaders([]);
        setTableTitle("");
        setAggregatedTitle(null);
        setAggregatedValue(null);
    }
  };

  /**
   * Prepare data list to show in table.
   * @param keyValue optional
   * @param keyName optional
   */
  const updateTableData = (keyValue?: string, keyName?: string) => {
    const levelTwo = selectedFilters?.levelTwo;
    const levelOne = selectedFilters?.levelOne;
    const dataArray = stateViewData?.data;
    const itemArray = parseStateData(
      dataArray,
      levelOne,
      levelTwo,
      keyValue,
      keyName
    );
    if (levelOne + "-" + levelTwo === COMBINED_LEVELS_FILTER.POSPm_LOCATION) {
      dispatch(
        handleStoreMarkers({ marker: MARKER_TYPE.POSPS, res: itemArray })
      );
      if (!markerType.includes(MARKER_TYPE.POSPS)) {
        setMarkersType(MARKER_TYPE.POSPS);
      }
    }
    setStateTableData(sortListItems(itemArray, "gridid", TABLE_SORT_ASC));
    setSortBy("gridid");
    setSortOrder(TABLE_SORT_ASC);
  };
  useEffect(() => {
    const levelTwo = selectedFilters?.levelTwo;
    const levelOne = selectedFilters?.levelOne;
    updateHeader(levelOne, levelTwo, stateViewData?.aggregated);
  }, [legendValues]);

  useEffect(() => {
    setIsLoading(false);
    setAggregatedTitle(null);
    setAggregatedValue(null);
    setAverageCatchmentRatio(null);

    if (
      stateViewData?.data &&
      !isGridViewSelected &&
      activeView === ACTIVE_VIEW_COUNTRY &&
      selectedFilters?.levelTwo !== REAL_TIME_LOCATION
    ) {
      const levelOne = selectedFilters?.levelOne;
      const levelTwo = selectedFilters?.levelTwo;
      const valueName = getValueKey(levelOne, levelTwo);
      const result = getStateIdWiseDataWithSum(
        stateViewData?.data,
        "sid",
        valueName,
        levelOne,
        levelTwo
      );
      setIdWiseData(result?.data);
      if (legendValues) {
        const keys = Object.keys(legendValues);
        const newLegendValues = {};
        if (keys.length) {
          keys.forEach((key) => {
            if (key.includes("min")) {
              newLegendValues[key] = result?.min;
            }
            if (key.includes("max")) {
              newLegendValues[key] = result?.max;
            }
          });
        }
        dispatch(updateLegendValue(newLegendValues));
      }
      setStateTableData(result?.array);
      setSortBy("total");
      setSortOrder(TABLE_SORT_ASC);
    } else if (stateViewData?.data) {
      setIdWiseData(
        getGridIdWiseData(
          stateViewData?.data || [],
          selectedFilters?.levelOne,
          selectedFilters?.levelTwo
        )
      );
      updateTableData();
    } else {
      setIdWiseData(null);
      setStateTableData(null);
    }
  }, [stateViewData]);

  const onFilterUpdate = () => {
    setCanShowToolTip(true);
    if (!selectedFilters?.gridNumber) {
      setMapClickedInfo(null);
    }
    let selectedState: any = null;
    let selectedDistrict: any = null;
    let selectedProduct: any = null;
    const seasonArray: string[] = [];

    if (selectedFilters?.state) {
      selectedState =
        selectedFilters?.state === COUNTRY
          ? selectedFilters?.state
          : getDataWithKeyValue(statesWithId, "vstate", selectedFilters?.state);
    }
    setSelectedState(selectedState);
    if (selectedFilters?.district) {
      selectedDistrict = getDataWithKeyValue(
        districtsWithId,
        "vdistrict",
        selectedFilters?.district
      );
    }
    setSelectedDistrict(selectedDistrict);

    if (selectedFilters?.product && selectedFilters?.product === ALL_PRODUCT) {
      selectedProduct = {
        productid: "",
        productname: "",
      };
    } else if (selectedFilters?.product) {
      selectedProduct = getDataWithKeyValue(
        productListWithId,
        "productname",
        selectedFilters?.product
      );
    }
    if (selectedFilters?.season && selectedFilters?.season?.length > 0) {
      selectedFilters?.season?.forEach((item: OptionType) => {
        seasonArray.push(item?.value);
      });
    }

    if (selectedState?.sid || selectedState === COUNTRY) {
      getStateViewData(
        selectedProduct?.productid,
        seasonArray,
        selectedFilters?.year,
        selectedState?.sid ? selectedState?.sid : "",
        selectedDistrict?.did
      );
    }
    dispatch(handleStateSelectedGeometry(null));
  };

  useEffect(() => {
    dispatch(handleStateGridLatLng(null));
    if (selectedFilters?.levelOne) {
      onFilterUpdate();
    }
  }, [selectedFilters, isGridViewSelected]);

  const getStateViewData = (
    productId: string | number,
    seasonArray: string[],
    season_year: string | number,
    stateId: string | number,
    districtId: string | number
  ) => {
    setIsLoading(true);
    const params = getRequestParams(
          selectedFilters,
          productId,
          seasonArray,
          season_year,
          stateId,
          districtId,
          gridId,
          mapClickedInfo
    )
    console.log(params);
    
    dispatch(
      getStateData(
params
      )
    );
  };
  const sortTableItems = (sortBy, sortOrder) => {
    setStateTableData(sortListItems(stateTableData, sortBy, sortOrder));
  };

  return (
    <div className="results-container">
      <div className="view-header mb-2">
        <span>
          {selectedFilters?.state !== COUNTRY
            ? `State Level View ${
                selectedFilters?.state && "- " + selectedFilters.state
              }`
            : "Country Level View"}
        </span>
      </div>
      <div className="map-table">
        {stateFilter === COUNTRY && selectedFilters?.levelOne !== "Weather" && (
          <div className="form-switch switch d-flex justify-content-center ">
            <label className="form-check-label">View: </label>
            <label className="form-check-label switch-text ms-1">State</label>
            <input
              className="form-check-input ms-1"
              type="checkbox"
              id="flexSwitchCheckDefault"
              checked={isGridViewSelected}
              onChange={() => {
                setGridViewSelected(!isGridViewSelected);
              }}
            />
            <label className="form-check-label switch-text ms-2">Grid</label>
          </div>
        )}

        {selectedFilters?.levelOne === "Weather" && (
          <>
            <div className="forecast-dropdown-layer">
              <WeatherLayerDropdown
                title="Weather Layer"
                items={items}
                selectedId={selected}
                onChange={(id) => {
                  //setSelected(id);
                  setSelected("precipitation");
                  console.log("Selected layer:", id);
                }}
              />
            </div>
            <div className="forecast-date-layer pt-2">
              <DateHourMinutePlayer
                today={moment().format(DATE_FORMAT.YYYY_MM_DD)}
                value={forecastDate}
                onChange={setForecastDate}
                stepMinutes={15}
                intervalMs={1000}
                loop={false}
                size="md"
              />
            </div>
          </>
        )}
        <div className="map">
          <Map
            activeView={activeView}
            markerType={markerType}
            markerData={markersStore}
            idWiseData={idWiseData}
            setGridId={setGridId}
            mapView={
              isGridViewSelected ? MAP_VIEW.GRID_WISE : MAP_VIEW.STATE_WISE
            }
            filterName={
              selectedFilters?.levelOne + "-" + selectedFilters?.levelTwo
            }
            legendValues={legendValues}
            legendStyle={legendStyle}
            distance={Number(distanceRange || 0)}
            district={selectedDistrict}
            state={selectedState}
            mapClickedInfo={mapClickedInfo}
            setMapClickedInfo={(mapInfo) => {
              dispatch(handleCountryGridLatLng(null));
              setMapClickedInfo(mapInfo);
            }}
            selectedGeometry={selectedGeometry}
            gridCenter={
              gridLatLng?.properties
                ? [
                    Number(gridLatLng?.properties?.lat_cen),
                    Number(gridLatLng?.properties?.lon_cen),
                  ]
                : null
            }
            onMarkerClick={(markerData, keyName) => {
              setGridId(markerData?.gridid);
              updateTableData(markerData[keyName], keyName);
            }}
            selectedFilters={selectedFilters}
            computedDate={computedDate}
            forecastDate={
              forecastDate.format(DATE_FORMAT.YYYY_MM_DD) ||
              moment().format(DATE_FORMAT.YYYY_MM_DD)
            }
          />
        </div>
        {stateTableData?.length > 0 && (
          <div className="result-table">
            <div className="table-header">
              <p className="view-header">{tableTitle}</p>
              <p className="view-header">
                {aggregatedTitle && (
                  <>
                    {aggregatedTitle}: <b>{aggregatedValue}</b>
                  </>
                )}
              </p>
              <p className="view-header">
                Total Count: <b>{stateTableData.length}</b>
              </p>
            </div>
            <div className="table-header">
              <p className="view-header" />
              <p className="view-header">
                {averageCatchmentRatio && (
                  <>
                    Average Catchment Ratio:{" "}
                    <b>{Number(averageCatchmentRatio).toFixed(2)}</b>
                  </>
                )}
              </p>
              <p className="view-header" />
            </div>
            <TableComponent
              data={stateTableData}
              columnHeaders={columnHeaders}
              onAction={(rowItem) => {
                if (
                  (stateFilter === COUNTRY && isGridViewSelected) ||
                  stateFilter !== COUNTRY
                ) {
                  setGridId(rowItem?.gridid);
                  updateTableData(rowItem?.gridid, "gridid");
                  dispatch(
                    getGridLatLng(
                      { gridid: rowItem?.gridid },
                      ACTIVE_VIEW_STATE
                    )
                  );
                }
              }}
              handleSort={sortTableItems}
              sortBy={sortBy}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              setSortBy={setSortBy}
              filterName=""
            />
          </div>
        )}
        {stateTableData && stateTableData?.length === 0 && !isLoading && (
          <div className="d-flex justify-content-center not-found">
            {stateFilter === COUNTRY && !isGridViewSelected && (
              <> There is no business in state.</>
            )}
            {(stateFilter !== COUNTRY || isGridViewSelected) && (
              <> There is no business in grid.</>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StateMap;
